import React, { useState, useEffect } from 'react';
import { Save, Briefcase } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import FormModalLayout from '../Common/FormModalLayout';
import { ensureRichTextValue, RichTextEditor } from '../RichTextEditor';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const PositionModal = ({ position, onClose, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  const isEditing = !!position;

  useEffect(() => {
    if (position) {
      setValue('name', position.name);
      setValue('description', ensureRichTextValue(position.description || ''));
      setValue('level', position.level);
      setValue('isActive', position.isActive === true || position.isActive === 'true' ? 'true' : 'false');
    } else {
      reset({
        name: '',
        description: '',
        level: '',
        isActive: 'true',
      });
    }
  }, [position, setValue, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving position:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormModalLayout
      title={isEditing ? 'Editar cargo' : 'Novo cargo'}
      subtitle="Nome, nível hierárquico e descrição das atribuições."
      headerIcon={Briefcase}
      onClose={onClose}
      size="lg"
      historyEntityType="positions"
      historyEntityId={position?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isLoading}>
            Cancelar
          </button>
          <Can permission={P.CONTACT_ACCESS}>
            <button
              type="submit"
              form="position-modal-form"
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : (
                <Save className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isEditing ? 'Atualizar' : 'Salvar'} cargo
            </button>
          </Can>
        </>
      }
    >
      <form id="position-modal-form" onSubmit={handleSubmit(onSubmit)} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="form-label form-label-required">Nome do cargo</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: Desenvolvedor pleno"
              {...register('name', { required: 'Nome do cargo é obrigatório' })}
            />
            {errors.name && <p className="form-error">{errors.name.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Nível</label>
            <select className="form-select" {...register('level', { required: 'Nível é obrigatório' })}>
              <option value="">Selecione o nível</option>
              <option value="JUNIOR">Júnior</option>
              <option value="PLENO">Pleno</option>
              <option value="SENIOR">Sênior</option>
              <option value="LEAD">Lead</option>
            </select>
            {errors.level && <p className="form-error">{errors.level.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select max-w-xs" {...register('isActive')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Descrição do cargo e responsabilidades..."
              />
            )}
          />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default PositionModal;
